import { Controller } from "@hotwired/stimulus"
import { track as mixpanelTrack } from "../lib/mixpanel"

// Phase 2.1 Tier 1 — controls the hierarchical budget tree. CRUD against
// the BudgetsController + friends over JSON; DOM patches applied in-place
// (no full-page reloads). Modals dismiss on backdrop click.
export default class extends Controller {
  static targets = [
    "tree",
    "newBudgetModal", "newBudgetKind", "newBudgetLabel",
    "groupModal", "groupModalTitle", "groupNameInput", "groupKindInput",
    "lineModal", "lineModalTitle", "lineCodeInput", "lineNameInput",
    "lineOriginalInput", "lineApprovedInput",
    "subModal", "subModalTitle", "subCodeInput", "subNameInput",
    "subOriginalInput", "subApprovedInput",
    "confirmModal", "confirmTitle", "confirmBody",
    "costSourcesModal", "costSourcesSubtitle", "costSourcesList",
    "costSourcesEmpty", "costSourceTypeSelect", "costSourceSearch",
    "costSourceCandidates", "costSourceFundSelect",
    "contractModal", "contractCategorySelect", "contractItemInput",
    "contractAmountInput", "contractDescriptionInput",
    "transferModal", "transferSourceLabel", "transferSourceBalance",
    "transferTargetSelect", "transferAmountInput", "transferReasonInput",
    "transferLogPanel", "transferLogSubtitle",
    "transferLogOriginal", "transferLogIn", "transferLogOut", "transferLogBalance",
    "transferLogList", "transferLogEmpty",
    "importModal", "importFileInput", "importModeNew", "importModeUpdate",
    "importErrors", "importPreview", "importPreviewBody", "importApplyButton",
    "seedSovModal", "seedFinancesModal",
    "budgetList", "deleteBudgetItem", "trashMenuItem", "trashCount", "trashModal", "trashList", "trashEmpty"
  ]

  static values = {
    projectId: Number,
    currentId: Number,
    currency: String,
    delimiter: String,
    separator: String,
    // E1-21 (Phelipe 2026-09-29): the budget trash's words.
    trashTitle: { type: String, default: "Move this budget to the trash?" },
    trashBody: { type: String, default: "It goes to Trash in this menu with everything in it, and you can restore it any time." },
    trashed: { type: String, default: "Budget moved to the trash" },
    setActive: { type: String, default: "Set active" }
  }

  connect() {
    this._csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    this._editingGroupId = null
    this._editingLineId = null
    this._editingSubId = null
    this._pendingLineGroupId = null
    this._pendingSubLineId = null
    this._pendingDelete = null
    this._showPendingFlash()
  }

  // ── Budget switcher ──────────────────────────────────────────────

  showNewBudgetModal() {
    this.newBudgetKindTarget.value = "working"
    this.newBudgetLabelTarget.value = ""
    this._show(this.newBudgetModalTarget)
  }

  hideNewBudgetModal() { this._hide(this.newBudgetModalTarget) }

  async createBudget() {
    const payload = {
      budget: {
        kind: this.newBudgetKindTarget.value,
        label: this.newBudgetLabelTarget.value
      }
    }
    const resp = await this._ajaxJSON("POST", `/projects/${this.projectIdValue}/budgets`, payload)
    if (resp.ok) {
      this._track("Budget Created", { budget_id: resp.body.id, via: "switcher" })
      this._flashAndRedirect("Budget created", resp.body.redirect_to)
    } else {
      this._showToast(resp.body?.errors?.join(", ") || "Failed to create budget", "error")
    }
  }

  async activateBudget(event) {
    const id = event.currentTarget.dataset.budgetIdParam
    const resp = await this._ajaxJSON("POST", `/projects/${this.projectIdValue}/budgets/${id}/activate`)
    if (resp.ok) {
      this._track("Budget Switched", { budget_id: parseInt(id, 10) })
      this._flashAndRedirect("Active budget updated", resp.body.redirect_to)
    } else {
      this._showToast(resp.body?.errors?.join(", ") || "Failed to activate budget", "error")
    }
  }

  async cloneBudget() {
    const resp = await this._ajaxJSON(
      "POST",
      `/projects/${this.projectIdValue}/budgets/${this.currentIdValue}/clone`,
      {}
    )
    if (resp.ok) {
      this._track("Budget Created", { budget_id: resp.body.id, via: "clone", source_budget_id: this.currentIdValue })
      this._flashAndRedirect("Budget cloned", resp.body.redirect_to)
    } else {
      this._showToast(resp.body?.errors?.join(", ") || "Failed to clone budget", "error")
    }
  }

  // E1-21: deleting a budget moves it to the project's trash (restorable).
  showDeleteBudgetModal() {
    this._pendingDelete = { kind: "budget", id: this.currentIdValue }
    this.confirmTitleTarget.textContent = this.trashTitleValue
    this.confirmBodyTarget.textContent = this.trashBodyValue
    this._show(this.confirmModalTarget)
  }

  // ── Budget trash (E1-21, EUN Church / Luma, Phelipe 2026-09-29) ─────
  // Deleted budgets wait here with everything in them, no expiry; Restore
  // puts one back (not active) and adds it to the switcher - no reload.

  showTrashModal() { this._show(this.trashModalTarget) }

  hideTrashModal() { this._hide(this.trashModalTarget) }

  async restoreBudget(event) {
    const button = event.currentTarget
    const id = parseInt(button.dataset.budgetIdParam, 10)
    button.disabled = true
    const resp = await this._ajaxJSON("POST", `/projects/${this.projectIdValue}/budgets/${id}/restore`)
    if (!resp.ok) {
      button.disabled = false
      this._showToast(resp.body?.error || resp.body?.errors?.join(", ") || "Failed to restore budget", "error")
      return
    }
    this.trashListTarget.querySelector(`[data-trashed-budget-id="${id}"]`)?.remove()
    const left = this.trashListTarget.querySelectorAll("[data-trashed-budget-id]").length
    this.trashCountTarget.textContent = String(left)
    this.trashEmptyTarget.classList.toggle("hidden", left > 0)
    this.trashMenuItemTarget.classList.toggle("hidden", left === 0)
    this._addToSwitcher(resp.body)
    this._showToast(resp.body.message || "Budget restored")
  }

  _addToSwitcher(budget) {
    if (!this.hasBudgetListTarget || !budget?.url) return
    const esc = (v) => String(v ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
    this.budgetListTarget.insertAdjacentHTML("beforeend", `
      <div class="flex items-center justify-between px-3 py-1 hover:bg-gray-50">
        <a href="${esc(budget.url)}" class="flex-1 flex items-center gap-2 text-gray-700">
          <span>${esc(budget.label)}</span>
          <span class="text-[11px] text-gray-400">(${esc(budget.kind)})</span>
        </a>
        <button type="button" data-action="click->budget#activateBudget" data-budget-id-param="${esc(budget.id)}"
                class="ml-2 text-[11px] text-brand hover:underline cursor-pointer">${esc(this.setActiveValue)}</button>
      </div>`)
    // Two budgets or more again: this one can be deleted.
    if (this.hasDeleteBudgetItemTarget) this.deleteBudgetItemTarget.classList.remove("hidden")
  }

  // ── Group CRUD ────────────────────────────────────────────────────

  showNewGroupModal() {
    this._editingGroupId = null
    this.groupModalTitleTarget.textContent = "Add Group"
    this.groupNameInputTarget.value = ""
    this.groupKindInputTarget.value = "hard"
    this._show(this.groupModalTarget)
  }

  showEditGroupModal(event) {
    const { groupIdParam, groupNameParam, groupParentKindParam } = event.currentTarget.dataset
    this._editingGroupId = parseInt(groupIdParam, 10)
    this.groupModalTitleTarget.textContent = "Edit Group"
    this.groupNameInputTarget.value = groupNameParam || ""
    this.groupKindInputTarget.value = groupParentKindParam || "hard"
    this._show(this.groupModalTarget)
  }

  hideGroupModal() { this._hide(this.groupModalTarget) }

  async saveGroup() {
    const payload = {
      budget_group: {
        name: this.groupNameInputTarget.value,
        parent_kind: this.groupKindInputTarget.value
      }
    }

    let resp
    if (this._editingGroupId) {
      resp = await this._ajaxJSON("PATCH", `/budget_groups/${this._editingGroupId}`, payload)
    } else {
      payload.budget_id = this.currentIdValue
      resp = await this._ajaxJSON("POST", `/budget_groups`, payload)
    }

    if (resp.ok) {
      this._hide(this.groupModalTarget)
      this._showToast(this._editingGroupId ? "Group updated" : "Group added")
      this._flashAndReload(this._editingGroupId ? "Group updated" : "Group added")
    } else {
      this._showToast(resp.body?.errors?.join(", ") || "Failed to save group", "error")
    }
  }

  showDeleteGroupModal(event) {
    const { groupIdParam, groupNameParam } = event.currentTarget.dataset
    this._pendingDelete = { kind: "group", id: parseInt(groupIdParam, 10) }
    this.confirmTitleTarget.textContent = "Delete group?"
    this.confirmBodyTarget.textContent =
      `"${groupNameParam}" and all its line items and sub items will be removed.`
    this._show(this.confirmModalTarget)
  }

  // ── Line item CRUD ────────────────────────────────────────────────

  showAddLineModal(event) {
    this._editingLineId = null
    this._pendingLineGroupId = parseInt(event.currentTarget.dataset.groupIdParam, 10)
    this.lineModalTitleTarget.textContent = "Add Line Item"
    this.lineCodeInputTarget.value = ""
    this.lineNameInputTarget.value = ""
    this.lineOriginalInputTarget.value = ""
    this.lineApprovedInputTarget.value = ""
    this._show(this.lineModalTarget)
  }

  showEditLineModal(event) {
    const { lineIdParam, lineCodeParam, lineNameParam, lineOriginalParam, lineApprovedParam } = event.currentTarget.dataset
    this._editingLineId = parseInt(lineIdParam, 10)
    this.lineModalTitleTarget.textContent = "Edit Line Item"
    this.lineCodeInputTarget.value = lineCodeParam || ""
    this.lineNameInputTarget.value = lineNameParam || ""
    this.lineOriginalInputTarget.value = lineOriginalParam || ""
    this.lineApprovedInputTarget.value = lineApprovedParam || ""
    this._show(this.lineModalTarget)
  }

  hideLineModal() { this._hide(this.lineModalTarget) }

  async saveLine() {
    const payload = {
      budget_line_item: {
        code: this.lineCodeInputTarget.value,
        name: this.lineNameInputTarget.value,
        original_amount: this.lineOriginalInputTarget.value || 0,
        approved_amount: this.lineApprovedInputTarget.value || this.lineOriginalInputTarget.value || 0
      }
    }

    let resp
    if (this._editingLineId) {
      resp = await this._ajaxJSON("PATCH", `/budget_line_items/${this._editingLineId}`, payload)
    } else {
      payload.budget_group_id = this._pendingLineGroupId
      resp = await this._ajaxJSON("POST", `/budget_line_items`, payload)
    }

    if (resp.ok) {
      this._hide(this.lineModalTarget)
      this._flashAndReload(this._editingLineId ? "Line item updated" : "Line item added")
    } else {
      this._showToast(resp.body?.errors?.join(", ") || "Failed to save line item", "error")
    }
  }

  showDeleteLineModal(event) {
    const { lineIdParam, lineNameParam } = event.currentTarget.dataset
    this._pendingDelete = { kind: "line", id: parseInt(lineIdParam, 10) }
    this.confirmTitleTarget.textContent = "Delete line item?"
    this.confirmBodyTarget.textContent =
      `"${lineNameParam}" and its sub items will be removed.`
    this._show(this.confirmModalTarget)
  }

  // ── Sub item CRUD ─────────────────────────────────────────────────

  showAddSubModal(event) {
    this._editingSubId = null
    this._pendingSubLineId = parseInt(event.currentTarget.dataset.lineIdParam, 10)
    this.subModalTitleTarget.textContent = "Add Sub Item"
    this.subCodeInputTarget.value = ""
    this.subNameInputTarget.value = ""
    this.subOriginalInputTarget.value = ""
    this.subApprovedInputTarget.value = ""
    this._show(this.subModalTarget)
  }

  showEditSubModal(event) {
    const { subIdParam, subCodeParam, subNameParam, subOriginalParam, subApprovedParam } = event.currentTarget.dataset
    this._editingSubId = parseInt(subIdParam, 10)
    this.subModalTitleTarget.textContent = "Edit Sub Item"
    this.subCodeInputTarget.value = subCodeParam || ""
    this.subNameInputTarget.value = subNameParam || ""
    this.subOriginalInputTarget.value = subOriginalParam || ""
    this.subApprovedInputTarget.value = subApprovedParam || ""
    this._show(this.subModalTarget)
  }

  hideSubModal() { this._hide(this.subModalTarget) }

  async saveSub() {
    const payload = {
      budget_sub_item: {
        code: this.subCodeInputTarget.value,
        name: this.subNameInputTarget.value,
        original_amount: this.subOriginalInputTarget.value || 0,
        approved_amount: this.subApprovedInputTarget.value || this.subOriginalInputTarget.value || 0
      }
    }

    let resp
    if (this._editingSubId) {
      resp = await this._ajaxJSON("PATCH", `/budget_sub_items/${this._editingSubId}`, payload)
    } else {
      payload.budget_line_item_id = this._pendingSubLineId
      resp = await this._ajaxJSON("POST", `/budget_sub_items`, payload)
    }

    if (resp.ok) {
      this._hide(this.subModalTarget)
      this._flashAndReload(this._editingSubId ? "Sub item updated" : "Sub item added")
    } else {
      this._showToast(resp.body?.errors?.join(", ") || "Failed to save sub item", "error")
    }
  }

  showDeleteSubModal(event) {
    const { subIdParam, subNameParam } = event.currentTarget.dataset
    this._pendingDelete = { kind: "sub", id: parseInt(subIdParam, 10) }
    this.confirmTitleTarget.textContent = "Delete sub item?"
    this.confirmBodyTarget.textContent = `"${subNameParam}" will be removed.`
    this._show(this.confirmModalTarget)
  }

  // ── Confirm delete dispatch ───────────────────────────────────────

  hideConfirmModal() { this._hide(this.confirmModalTarget) }

  async confirmDelete() {
    if (!this._pendingDelete) return
    const { kind, id } = this._pendingDelete

    let url
    switch (kind) {
      case "budget": url = `/projects/${this.projectIdValue}/budgets/${id}`; break
      case "group":  url = `/budget_groups/${id}`; break
      case "line":   url = `/budget_line_items/${id}`; break
      case "sub":    url = `/budget_sub_items/${id}`; break
      default: return
    }

    const resp = await this._ajaxJSON("DELETE", url)
    this._hide(this.confirmModalTarget)
    this._pendingDelete = null

    if (resp.ok) {
      if (kind === "budget" && resp.body?.redirect_to) {
        this._flashAndRedirect(this.trashedValue, resp.body.redirect_to)
      } else {
        this._flashAndReload(`${this._kindLabel(kind)} deleted`)
      }
    } else {
      this._showToast(resp.body?.errors?.join(", ") || `Failed to delete ${this._kindLabel(kind).toLowerCase()}`, "error")
    }
  }

  // ── Cost sources (link/unlink) ────────────────────────────────────

  async showCostSourcesModal(event) {
    const { lineIdParam, lineNameParam } = event.currentTarget.dataset
    this._costSourceLineId = parseInt(lineIdParam, 10)
    this.costSourcesSubtitleTarget.textContent = lineNameParam || ""
    this._costSourceCandidates = null
    this.costSourcesListTarget.querySelectorAll("[data-cost-source-row]").forEach(el => el.remove())
    this.costSourcesEmptyTarget.classList.remove("hidden")
    this.costSourceSearchTarget.value = ""
    this._show(this.costSourcesModalTarget)

    const resp = await this._ajaxJSON("GET", `/budget_line_items/${this._costSourceLineId}/cost_sources`)
    if (resp.ok) {
      this._renderCostSourcesList(resp.body.current || [])
      this._costSourceCandidates = resp.body.candidates || {}
      this.renderCostSourceCandidates()
    } else {
      this._showToast("Failed to load cost sources", "error")
    }
  }

  renderCostSourceCandidates() {
    if (!this._costSourceCandidates) return

    const type = this.costSourceTypeSelectTarget.value
    const filter = (this.costSourceSearchTarget.value || "").toLowerCase().trim()
    const linkedIds = new Set(
      Array.from(this.costSourcesListTarget.querySelectorAll("[data-cost-source-row]"))
        .filter(el => el.dataset.sourceType === type)
        .map(el => parseInt(el.dataset.sourceId, 10))
    )

    const all = this._costSourceCandidates[type] || []
    const filtered = all.filter(c => {
      if (linkedIds.has(c.id)) return false
      if (!filter) return true
      const label = `${c.label || ""} ${c.sub_label || ""}`.toLowerCase()
      return label.includes(filter)
    })

    const container = this.costSourceCandidatesTarget
    container.innerHTML = ""
    if (filtered.length === 0) {
      const empty = document.createElement("div")
      empty.className = "px-3 py-2 text-[13px] text-gray-500 italic"
      empty.textContent = "No matching sources"
      container.appendChild(empty)
      return
    }

    filtered.slice(0, 100).forEach(c => {
      const row = document.createElement("button")
      row.type = "button"
      row.className = "w-full flex items-center justify-between px-3 py-1.5 text-left hover:bg-blue-50 text-[13px] cursor-pointer"
      row.dataset.action = "click->budget#linkCostSource"
      row.dataset.sourceId = c.id
      row.innerHTML = `
        <div class="min-w-0">
          <div class="text-gray-800 truncate">${this._escape(c.label || '(no label)')}</div>
          ${c.sub_label ? `<div class="text-gray-500 text-[11px] truncate">${this._escape(c.sub_label)}</div>` : ""}
        </div>
        <span class="text-gray-500 text-[11px] ml-2 whitespace-nowrap">${this._formatCurrency(c.amount || 0)}</span>
      `
      container.appendChild(row)
    })
  }

  async linkCostSource(event) {
    const row = event.currentTarget
    const sourceId = parseInt(row.dataset.sourceId, 10)
    const sourceType = this.costSourceTypeSelectTarget.value

    const fundSourceId = this.hasCostSourceFundSelectTarget
      ? this.costSourceFundSelectTarget.value
      : ""

    const resp = await this._ajaxJSON("POST", "/budget_cost_sources", {
      budget_cost_source: {
        budget_line_item_id: this._costSourceLineId,
        source_type: sourceType,
        source_id: sourceId,
        fund_source_id: fundSourceId || null
      }
    })

    if (resp.ok) {
      this._track("Cost Source Linked", { source_type: sourceType })
      this._appendCostSourceRow(resp.body)
      this.renderCostSourceCandidates()
      this._costSourcesChanged = true
    } else {
      this._showToast(resp.body?.errors?.join(", ") || "Failed to link source", "error")
    }
  }

  async unlinkCostSource(event) {
    const row = event.currentTarget.closest("[data-cost-source-row]")
    if (!row) return
    const id = row.dataset.sourceRowId
    const sourceType = row.dataset.sourceType

    const resp = await this._ajaxJSON("DELETE", `/budget_cost_sources/${id}`)
    if (resp.ok) {
      this._track("Cost Source Unlinked", { source_type: sourceType })
      row.remove()
      this._updateCostSourcesEmptyState()
      this.renderCostSourceCandidates()
      this._costSourcesChanged = true
    } else {
      this._showToast(resp.body?.errors?.join(", ") || "Failed to unlink source", "error")
    }
  }

  _renderCostSourcesList(current) {
    // Clear existing rows (keep the empty placeholder).
    this.costSourcesListTarget.querySelectorAll("[data-cost-source-row]").forEach(el => el.remove())
    current.forEach(cs => this._appendCostSourceRow(cs))
  }

  _appendCostSourceRow(cs) {
    const row = document.createElement("div")
    row.dataset.costSourceRow = ""
    row.dataset.sourceRowId = cs.id
    row.dataset.sourceType = cs.source_type
    row.dataset.sourceId = cs.source_id
    row.className = "flex items-center justify-between bg-blue-50 border border-blue-100 rounded-md px-2 py-1"
    row.innerHTML = `
      <div class="min-w-0">
        <div class="text-[11px] uppercase tracking-wide text-blue-700">${this._escape(cs.type_label || "")}</div>
        <div class="text-[13px] text-gray-800 truncate">${this._escape(cs.label || "")}</div>
      </div>
      <button type="button" class="text-gray-500 hover:text-red-600 cursor-pointer ml-2 p-1"
              data-action="click->budget#unlinkCostSource" title="Unlink">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
        </svg>
      </button>
    `
    this.costSourcesListTarget.appendChild(row)
    this._updateCostSourcesEmptyState()
  }

  _updateCostSourcesEmptyState() {
    const hasRows = this.costSourcesListTarget.querySelector("[data-cost-source-row]") !== null
    this.costSourcesEmptyTarget.classList.toggle("hidden", hasRows)
  }

  // When the cost-sources modal closes *after* changes, reload to pick up
  // the freshly computed spend totals. Keeps the rollup honest without
  // duplicating the calculator in JS.
  hideCostSourcesModal() {
    this._hide(this.costSourcesModalTarget)
    if (this._costSourcesChanged) {
      this._costSourcesChanged = false
      this._flashAndReload("Cost sources updated")
    }
  }

  // ── Budget-to-contract ────────────────────────────────────────────

  showContractModal(event) {
    const { lineIdParam, lineNameParam, lineCodeParam, lineAmountParam } = event.currentTarget.dataset
    this._contractLineId = parseInt(lineIdParam, 10)
    this.contractItemInputTarget.value = lineCodeParam || ""
    this.contractDescriptionInputTarget.value = lineNameParam || ""
    this.contractAmountInputTarget.value = lineAmountParam || ""
    this._show(this.contractModalTarget)
  }

  hideContractModal() { this._hide(this.contractModalTarget) }

  async saveContract() {
    if (!this.contractCategorySelectTarget.value) {
      this._showToast("Pick an SOV category", "error")
      return
    }

    const payload = {
      sov_category_id: this.contractCategorySelectTarget.value,
      item: this.contractItemInputTarget.value,
      description: this.contractDescriptionInputTarget.value,
      amount: parseFloat(this.contractAmountInputTarget.value) || 0
    }

    const resp = await this._ajaxJSON(
      "POST",
      `/budget_line_items/${this._contractLineId}/contracts`,
      payload
    )

    if (resp.ok) {
      this._track("Budget-to-Contract Created", {
        budget_line_item_id: this._contractLineId,
        sov_line_item_id: resp.body.sov_line_item.id
      })
      this._hide(this.contractModalTarget)
      this._flashAndReload("SOV line item created and linked")
    } else {
      this._showToast(resp.body?.errors?.join(", ") || "Failed to create SOV line item", "error")
    }
  }

  // ── Seed from SOV (index-page action) ─────────────────────────────

  showSeedFromSovModal() { this._show(this.seedSovModalTarget) }
  hideSeedFromSovModal() { this._hide(this.seedSovModalTarget) }

  async confirmSeedFromSov() {
    this._hide(this.seedSovModalTarget)
    const resp = await this._ajaxJSON("POST", `/projects/${this.projectIdValue}/budgets/seed_from_sov`)
    if (resp.ok) {
      this._track("Budget Seeded From SOV", { budget_id: resp.body.id })
      this._flashAndRedirect("Working budget seeded from SOV", resp.body.redirect_to)
    } else {
      this._showToast(resp.body?.errors?.join(", ") || "Failed to seed budget", "error")
    }
  }

  showSeedFromFinancesModal() { this._show(this.seedFinancesModalTarget) }
  hideSeedFromFinancesModal() { this._hide(this.seedFinancesModalTarget) }

  async confirmSeedFromFinances() {
    this._hide(this.seedFinancesModalTarget)
    const resp = await this._ajaxJSON("POST", `/projects/${this.projectIdValue}/budgets/seed_from_finances`)
    if (resp.ok) {
      this._track("Budget Seeded From Finances", { budget_id: resp.body.id })
      this._flashAndRedirect("Budget seeded from Finances", resp.body.redirect_to)
    } else {
      this._showToast(resp.body?.errors?.join(", ") || "Failed to seed budget", "error")
    }
  }

  // ── Transfers ─────────────────────────────────────────────────────

  showTransferModal(event) {
    const { lineIdParam, lineNameParam, lineBalanceParam } = event.currentTarget.dataset
    this._transferSourceLineId = parseInt(lineIdParam, 10)
    this._transferSourceBalance = parseFloat(lineBalanceParam) || 0
    this.transferSourceLabelTarget.textContent = lineNameParam || ""
    this.transferSourceBalanceTarget.textContent = this._formatCurrency(this._transferSourceBalance)
    this.transferAmountInputTarget.value = ""
    this.transferAmountInputTarget.max = this._transferSourceBalance.toFixed(2)
    this.transferReasonInputTarget.value = ""
    this.transferTargetSelectTarget.value = ""
    // Remove the source from the target dropdown so the user can't pick it.
    Array.from(this.transferTargetSelectTarget.options).forEach(opt => {
      opt.disabled = (parseInt(opt.value, 10) === this._transferSourceLineId)
    })
    this._show(this.transferModalTarget)
  }

  hideTransferModal() { this._hide(this.transferModalTarget) }

  async saveTransfer() {
    const targetId = parseInt(this.transferTargetSelectTarget.value, 10)
    if (!targetId) {
      this._showToast("Pick a target line", "error")
      return
    }
    const amount = parseFloat(this.transferAmountInputTarget.value)
    if (!amount || amount <= 0) {
      this._showToast("Enter an amount greater than zero", "error")
      return
    }
    if (amount > this._transferSourceBalance + 0.001) {
      this._showToast("Amount exceeds the source line's balance", "error")
      return
    }

    const resp = await this._ajaxJSON("POST", "/budget_transfers", {
      budget_transfer: {
        source_line_item_id: this._transferSourceLineId,
        target_line_item_id: targetId,
        amount,
        reason: this.transferReasonInputTarget.value
      }
    })

    if (resp.ok) {
      this._track("Budget Transfer Created", {
        budget_transfer_id: resp.body.id,
        amount,
        from_contingency: !!resp.body.from_contingency
      })
      if (resp.body.from_contingency) {
        this._track("Contingency Drawn", {
          budget_transfer_id: resp.body.id,
          amount
        })
      }
      this._hide(this.transferModalTarget)
      this._flashAndReload("Transfer recorded")
    } else {
      this._showToast(resp.body?.errors?.join(", ") || "Failed to record transfer", "error")
    }
  }

  async showTransferLog(event) {
    const { lineIdParam, lineNameParam } = event.currentTarget.dataset
    this._transferLogLineId = parseInt(lineIdParam, 10)
    this.transferLogSubtitleTarget.textContent = lineNameParam || ""
    this.transferLogListTarget.innerHTML = ""
    this.transferLogEmptyTarget.classList.remove("hidden")
    this.transferLogOriginalTarget.textContent = "—"
    this.transferLogInTarget.textContent = "—"
    this.transferLogOutTarget.textContent = "—"
    this.transferLogBalanceTarget.textContent = "—"
    this._show(this.transferLogPanelTarget)

    const resp = await this._ajaxJSON("GET", `/budget_line_items/${this._transferLogLineId}/transfers`)
    if (!resp.ok) {
      this._showToast("Failed to load transfer log", "error")
      return
    }

    const { totals, incoming, outgoing } = resp.body
    this.transferLogOriginalTarget.textContent = this._formatCurrency(totals.original)
    this.transferLogInTarget.textContent = this._formatCurrency(totals.in_total)
    this.transferLogOutTarget.textContent = this._formatCurrency(totals.out_total)
    this.transferLogBalanceTarget.textContent = this._formatCurrency(totals.balance)

    // Merge in + out, sort chronologically, compute running net.
    const events = [...incoming, ...outgoing].sort((a, b) =>
      new Date(a.transferred_at) - new Date(b.transferred_at)
    )
    if (events.length === 0) {
      this.transferLogEmptyTarget.classList.remove("hidden")
      return
    }
    this.transferLogEmptyTarget.classList.add("hidden")

    let running = totals.original
    events.forEach(ev => {
      running += (ev.direction === "in" ? ev.amount : -ev.amount)
      this.transferLogListTarget.appendChild(this._buildTransferRow(ev, running))
    })
  }

  hideTransferLog() { this._hide(this.transferLogPanelTarget) }

  _buildTransferRow(ev, running) {
    const li = document.createElement("li")
    li.className = "py-2"
    const isIn = ev.direction === "in"
    const color = isIn ? "text-green-700" : "text-red-700"
    const sign = isIn ? "+" : "−"
    const dt = new Date(ev.transferred_at)
    const dateStr = isNaN(dt) ? "" : dt.toISOString().slice(0, 10)

    li.innerHTML = `
      <div class="flex items-start justify-between gap-3">
        <div class="min-w-0">
          <div class="text-[11px] uppercase tracking-wide ${color}">
            ${isIn ? "Transfer In" : "Transfer Out"}${ev.from_contingency ? " · Contingency" : ""}
          </div>
          <div class="text-[13px] text-gray-800 truncate">
            ${isIn ? "From " : "To "}<span class="font-medium">${this._escape(ev.other_line_name || "")}</span>
            ${ev.other_line_code ? `<span class="text-gray-500 text-[11px] ml-1">(${this._escape(ev.other_line_code)})</span>` : ""}
          </div>
          ${ev.reason ? `<div class="text-[13px] text-gray-500 mt-0.5">${this._escape(ev.reason)}</div>` : ""}
          <div class="text-[11px] text-gray-400 mt-0.5">${dateStr}${ev.requested_by ? " · " + this._escape(ev.requested_by) : ""}</div>
        </div>
        <div class="text-right whitespace-nowrap">
          <div class="${color} font-medium text-[13px]">${sign}${this._formatCurrency(ev.amount)}</div>
          <div class="text-[11px] text-gray-500">Running: ${this._formatCurrency(running)}</div>
        </div>
      </div>
    `
    return li
  }

  _formatCurrency(amount) {
    const n = Number(amount || 0)
    const delim = this.hasDelimiterValue && this.delimiterValue ? this.delimiterValue : ","
    const sep = this.hasSeparatorValue && this.separatorValue ? this.separatorValue : "."
    const sym = this.hasCurrencyValue && this.currencyValue ? this.currencyValue : "$"
    const fixed = n.toFixed(2)
    const [intPart, fracPart] = fixed.split(".")
    const withDelim = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, delim)
    return `${sym}${withDelim}${sep}${fracPart}`
  }

  _escape(str) {
    return String(str ?? "").replace(/[&<>"']/g, c => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
    }[c]))
  }

  // ── Group collapse/expand ─────────────────────────────────────────

  toggleGroup(event) {
    const id = event.currentTarget.dataset.groupIdParam
    const body = this.element.querySelector(`[data-budget-target='groupBody-${id}']`)
    if (!body) return
    body.classList.toggle("hidden")
  }

  stopPropagation(event) { event.stopPropagation() }

  // ── Toast / flash helpers ─────────────────────────────────────────

  _flashAndReload(message, type = "success") {
    sessionStorage.setItem("budget_flash", JSON.stringify({ message, type }))
    window.location.reload()
  }

  _flashAndRedirect(message, url, type = "success") {
    sessionStorage.setItem("budget_flash", JSON.stringify({ message, type }))
    window.location.href = url
  }

  _showPendingFlash() {
    const raw = sessionStorage.getItem("budget_flash")
    if (!raw) return
    sessionStorage.removeItem("budget_flash")
    try {
      const { message, type } = JSON.parse(raw)
      this._showToast(message, type)
    } catch { /* ignore */ }
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const iconSvg = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = iconSvg
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }

  // ── AJAX helper ───────────────────────────────────────────────────

  async _ajaxJSON(method, url, payload) {
    const options = {
      method,
      headers: {
        "X-CSRF-Token": this._csrfToken,
        "Accept": "application/json"
      }
    }
    if (payload !== undefined && method !== "GET") {
      options.headers["Content-Type"] = "application/json"
      options.body = JSON.stringify(payload)
    }

    const resp = await fetch(url, options)
    let body = null
    try {
      if (resp.headers.get("content-type")?.includes("application/json")) {
        body = await resp.json()
      }
    } catch { /* ignore */ }
    return { ok: resp.ok, status: resp.status, body }
  }

  _show(el) { el.classList.remove("hidden") }
  _hide(el) { el.classList.add("hidden") }

  _kindLabel(kind) {
    switch (kind) {
      case "budget": return "Budget"
      case "group":  return "Group"
      case "line":   return "Line item"
      case "sub":    return "Sub item"
      default: return "Item"
    }
  }

  _track(event, props = {}) {
    try {
      mixpanelTrack(event, { project_id: this.projectIdValue, ...props })
    } catch { /* mixpanel optional */ }
  }

  // ── Excel import ──────────────────────────────────────────────────

  showImportModal() {
    this.importFileInputTarget.value = ""
    this._pendingImportFile = null
    this._lastPreviewHasErrors = false
    this.importApplyButtonTarget.disabled = true
    this._hide(this.importErrorsTarget)
    this._hide(this.importPreviewTarget)
    this.importErrorsTarget.innerHTML = ""
    this.importPreviewBodyTarget.innerHTML = ""
    this._show(this.importModalTarget)
    this._track("Budget Import Started")
  }

  hideImportModal() { this._hide(this.importModalTarget) }

  async previewImport(event) {
    const file = event.currentTarget.files && event.currentTarget.files[0]
    if (!file) return

    this._pendingImportFile = file
    this.importApplyButtonTarget.disabled = true
    this.importErrorsTarget.innerHTML = ""
    this._hide(this.importErrorsTarget)
    this.importPreviewBodyTarget.innerHTML = ""
    this._hide(this.importPreviewTarget)

    const form = new FormData()
    form.append("file", file)

    const resp = await fetch(`/projects/${this.projectIdValue}/budgets/import/preview`, {
      method: "POST",
      headers: { "X-CSRF-Token": this._csrfToken, "Accept": "application/json" },
      body: form
    })
    let body = null
    try { body = await resp.json() } catch { /* ignore */ }
    if (!resp.ok || !body) {
      this._renderImportErrors([{ row: 0, message: "Preview failed — could not parse file." }])
      return
    }

    this._renderImportPreview(body.groups || [])
    if ((body.errors || []).length) {
      this._renderImportErrors(body.errors)
      this._lastPreviewHasErrors = true
      this.importApplyButtonTarget.disabled = true
    } else {
      this._lastPreviewHasErrors = false
      this.importApplyButtonTarget.disabled = false
    }
  }

  _renderImportPreview(groups) {
    const body = this.importPreviewBodyTarget
    body.innerHTML = ""
    if (!groups.length) {
      this._hide(this.importPreviewTarget)
      return
    }
    groups.forEach((g, gi) => {
      body.appendChild(this._importGroupRow(g, gi))
      ;(g.lines || []).forEach(l => {
        body.appendChild(this._importRow("Line Item", l.code || "", l.name, "", this._formatNum(l.original_amount), this._formatNum(l.approved_amount)))
        ;(l.subs || []).forEach(s => {
          body.appendChild(this._importRow("Sub Item", s.code || "", s.name, "", this._formatNum(s.original_amount), this._formatNum(s.approved_amount)))
        })
      })
    })
    this._show(this.importPreviewTarget)
  }

  // Group rows carry an editable cost category. The sheet's own value (or
  // the AI's guess) is preselected; whatever the user leaves here is sent
  // with the apply request and wins over both.
  _importGroupRow(group, index) {
    const kinds = [
      ["hard", "Hard costs"], ["soft", "Soft costs"], ["ffe", "FF&E"],
      ["contingency", "Contingency"], ["other", "Other"]
    ]
    const current = group.parent_kind || "other"
    const options = kinds
      .map(([value, label]) => `<option value="${value}"${value === current ? " selected" : ""}>${label}</option>`)
      .join("")

    const tr = document.createElement("tr")
    tr.innerHTML = `
      <td class="px-2 py-1 text-[11px] uppercase tracking-wide text-gray-500">Group</td>
      <td class="px-2 py-1"></td>
      <td class="px-2 py-1 font-semibold text-gray-800">${this._escape(group.name)}</td>
      <td class="px-2 py-1">
        <select data-import-kind data-group-index="${index}"
                class="text-[13px] border border-gray-300 rounded px-1 py-0.5 bg-white cursor-pointer">
          ${options}
        </select>
      </td>
      <td class="px-2 py-1"></td>
      <td class="px-2 py-1"></td>
    `
    return tr
  }

  _importRow(level, code, name, kind, original, approved) {
    const tr = document.createElement("tr")
    const levelClass = level === "Group" ? "font-semibold text-gray-800" : (level === "Sub Item" ? "text-gray-600 pl-6" : "text-gray-700 pl-3")
    tr.innerHTML = `
      <td class="px-2 py-1 text-[11px] uppercase tracking-wide text-gray-500">${this._escape(level)}</td>
      <td class="px-2 py-1">${this._escape(code)}</td>
      <td class="px-2 py-1 ${levelClass}">${this._escape(name)}</td>
      <td class="px-2 py-1 text-[11px] text-gray-600">${this._escape(kind || "")}</td>
      <td class="px-2 py-1 text-right">${this._escape(original || "")}</td>
      <td class="px-2 py-1 text-right">${this._escape(approved || "")}</td>
    `
    return tr
  }

  _renderImportErrors(errors) {
    this.importErrorsTarget.innerHTML = ""
    errors.forEach(err => {
      const line = document.createElement("div")
      line.className = "text-[13px]"
      const rowPart = (err.row && err.row > 0) ? `Row ${err.row}: ` : ""
      line.textContent = `${rowPart}${err.message}`
      this.importErrorsTarget.appendChild(line)
    })
    this._show(this.importErrorsTarget)
  }

  _formatNum(n) {
    if (n === null || n === undefined || n === "") return ""
    return Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  async applyImport() {
    if (!this._pendingImportFile || this._lastPreviewHasErrors) return

    // The empty state renders the modal without the mode radios — there
    // is no budget to replace, so "new" is the only possible mode.
    const mode = this.hasImportModeUpdateTarget && this.importModeUpdateTarget.checked ? "update" : "new"
    const form = new FormData()
    form.append("file", this._pendingImportFile)
    form.append("mode", mode)
    if (mode === "update") {
      form.append("budget_id", String(this.currentIdValue))
    }

    const kindOverrides = {}
    this.importPreviewBodyTarget.querySelectorAll("[data-import-kind]").forEach(select => {
      kindOverrides[select.dataset.groupIndex] = select.value
    })
    if (Object.keys(kindOverrides).length) {
      form.append("kind_overrides", JSON.stringify(kindOverrides))
    }

    this.importApplyButtonTarget.disabled = true

    const resp = await fetch(`/projects/${this.projectIdValue}/budgets/import`, {
      method: "POST",
      headers: { "X-CSRF-Token": this._csrfToken, "Accept": "application/json" },
      body: form
    })
    let body = null
    try { body = await resp.json() } catch { /* ignore */ }
    if (resp.ok && body && body.redirect_to) {
      this._track("Budget Imported", { budget_id: body.id, import_mode: mode })
      this._flashAndRedirect("Budget imported", body.redirect_to)
    } else {
      this.importApplyButtonTarget.disabled = false
      const errors = (body && body.errors) || [{ row: 0, message: "Import failed." }]
      this._renderImportErrors(errors)
    }
  }
}
